import { useState } from 'react';

import { CONTRACT_TYPE, FORBIDDEN_ERROR_STATUS, UNAUTHORIZED_ERROR_STATUS } from '../../constants';
import { useTokenStore, useUserInfoStore, useWorkInfoStore } from '../../store';
import { initialWorkInfoState } from '~/store/workInfo/useWorkInfoStore';
import type { TypeErrorFormat } from '~/utils/fetchHelper';

import { useGetCommuteInfo, useGetUserLatestContract, useUserInfo } from '../api';

/** 인증 실패(401/403) 에러 여부. 인터셉터가 토큰 갱신을 시도하나, 갱신까지 실패하면 Entry가 로그인으로 보낸다. */
const isAuthError = (error?: TypeErrorFormat | null) =>
  [UNAUTHORIZED_ERROR_STATUS, FORBIDDEN_ERROR_STATUS].includes(String(error?.code));

/**
 * @description - 앱 최초 실행 시, 분기 조건을 판단하여 이동되어야 할 페이지의 경로를 반환하는 커스텀훅
 * @remarks 토큰 존재 여부, 계약 구분, 로그인 여부 등을 판단하여 페이지 이동 경로를 반환합니다.
 * - 토큰이 존재하지 않을 때 : 로그인 페이지로 이동
 * - 계약 구분이 아르바이트일 때 : 현재 계약 상태이면 출근 페이지로 이동, 아니면 내부망 연결 페이지로 이동
 * - 계약 구분이 상용직일 때 : 현재 출근 상태이면 출근 페이지로 이동, 아니면 내부망 연결 페이지로 이동
 * - 토큰이 존재하지만 API 요청 실패했을 때
 *    - 인증 실패 (http status 401, 403)인 경우 : 세션 무효로 보고 로그인 페이지로 이동
 *    - 이외의 경우 : Entry 전용 에러 화면으로 이동
 */
export const useGetEntryRedirectPath = () => {
  const [redirectPath, setRedirectPath] = useState<null | string>(null);
  const [isSuccess, setIsSuccess] = useState<null | boolean>(null);
  const [entryError, setEntryError] = useState<unknown>(null);

  const accessToken = useTokenStore((state) => state.accessToken);

  const { setUserInfo } = useUserInfoStore((state) => state.actions);
  const { setWorkInfo } = useWorkInfoStore((state) => state.actions);

  const requestGetUserInfo = useUserInfo();
  const requestGetCommuteInfo = useGetCommuteInfo();
  const requestGetUserLatestContract = useGetUserLatestContract();

  const handleUnauthorizedError = () => {
    setRedirectPath('/login');
    setIsSuccess(false);
  };

  const getEntryRedirectPath = async () => {
    setEntryError(null);

    if (!accessToken) {
      setRedirectPath('/login');
      setIsSuccess(true);
      return;
    }

    const userInfoError = await getUserInfo();

    if (isAuthError(userInfoError)) {
      handleUnauthorizedError();
      return;
    }
    if (userInfoError) {
      setEntryError(userInfoError.rawError);
      return;
    }

    const { status: currentCommuteStatus, error: commuteError } = await getIsCurrentCommute();

    if (isAuthError(commuteError)) {
      handleUnauthorizedError();
      return;
    }
    if (commuteError) {
      setEntryError(commuteError.rawError);
      return;
    }

    const { contractType, loginId } = useUserInfoStore.getState();

    if (contractType === CONTRACT_TYPE.SHORT) {
      if (currentCommuteStatus === null || typeof currentCommuteStatus === 'undefined') {
        setEntryError(new Error('출근 상태를 확인할 수 없습니다.'));
        return;
      }

      const needToContract = currentCommuteStatus === 'AVAILABLE_LABOR_CONTRACT';
      if (!needToContract) {
        await requestGetUserLatestContract({
          showLoading: false,
          onSuccess: (response) => {
            if (response) {
              setUserInfo({ latestContract: response.data });
            }
          },
          onError: (error) => {
            console.error('>> 최신 계약 정보 조회 실패', error);
          },
        });
      }
      setRedirectPath(needToContract ? '/before-work' : '/start-work');
      setIsSuccess(true);
      return;
    }

    if (contractType === CONTRACT_TYPE.CONTRACT && loginId !== '') {
      if (currentCommuteStatus === null || typeof currentCommuteStatus === 'undefined') {
        setEntryError(new Error('출근 상태를 확인할 수 없습니다.'));
        return;
      }

      setRedirectPath('/start-work');
      setIsSuccess(true);
      return;
    }

    setRedirectPath('/login');
    setIsSuccess(false);
  };

  const getUserInfo = async (): Promise<TypeErrorFormat | null> => {
    return new Promise<TypeErrorFormat | null>((resolve) =>
      requestGetUserInfo({
        showLoading: false,
        onSuccess: (response) => {
          if (response) {
            setUserInfo(response);
          }
          resolve(null);
        },
        onError: (error) => {
          resolve(error);
        },
      })
    );
  };

  const getIsCurrentCommute = async (): Promise<{ status?: string | null; error: TypeErrorFormat | null }> => {
    return new Promise<{ status?: string | null; error: TypeErrorFormat | null }>((resolve) =>
      requestGetCommuteInfo({
        showLoading: false,
        onSuccess: (response) => {
          const { status, data, recentCheckInOut, workType } = response.data;

          setWorkInfo({
            ...(data ?? initialWorkInfoState),
            status,
            recentCheckInOut,
            workType,
          });
          resolve({ status: status?.code, error: null });
        },
        onError: (error) => {
          resolve({ error });
        },
      })
    );
  };

  return { redirectPath, isSuccess, entryError, getEntryRedirectPath };
};
